import {
    Autocomplete,
    Button,
    Group,
    Paper,
    Select,
    Stack,
    Text,
    Textarea,
    TextInput,
    Title,
} from '@mantine/core'
import {DatePickerInput} from '@mantine/dates'
import {useForm} from '@mantine/form'
import {useEffect, useState} from 'react'

import type {ProjectBillingTerms} from '../projects/schemas'
import {
    isScheduleLockedServiceJob,
    SCHEDULE_LOCKED_JOB_HINT,
} from '../service-schedules/preview-labels'
import {useActiveServiceTypes} from '../settings/use-service-types'
import {FoldedText} from '../ui/folded-text'
import {projectBillingTermsLabel} from './billing-terms-display'
import {useEditRights} from './edit-rights-context'
import type {JobDataLayer} from './job-data-layer'
import {
    formatJobDate,
    parseApiDate,
    parseDateInputValue,
    toApiDateString,
} from './job-schedule-format'
import type {JobDetail} from './types'

type JobDetailsSectionProps = {
    job: JobDetail
    jobDataLayer: JobDataLayer
    onJobUpdated: (job: JobDetail) => void
    billingTerms?: ProjectBillingTerms[]
}

type JobDetailsFormValues = {
    serviceTypeId: string | null
    specificType: string
    plannedEndDate: Date | null
    notes: string
    poNumber: string
}

function jobToFormValues(job: JobDetail): JobDetailsFormValues {
    return {
        serviceTypeId: job.serviceTypeId,
        specificType: job.specificType ?? '',
        plannedEndDate: job.plannedEndDate ? parseApiDate(job.plannedEndDate) : null,
        notes: job.notes ?? '',
        poNumber: job.poNumber ?? '',
    }
}

export function JobDetailsSection({
    job,
    jobDataLayer,
    onJobUpdated,
    billingTerms = [],
}: JobDetailsSectionProps) {
    const {canEdit} = useEditRights()
    const [isEditing, setIsEditing] = useState(false)
    const [saving, setSaving] = useState(false)
    const [errorMessage, setErrorMessage] = useState<string | null>(null)
    const {data: serviceTypes = []} = useActiveServiceTypes()
    const scheduleLocked = isScheduleLockedServiceJob(job.scheduleRole)
    const jobBillingTerms = billingTerms.find((terms) => terms.id === job.projectBillingTermsId)

    const form = useForm<JobDetailsFormValues>({
        initialValues: jobToFormValues(job),
        validate: (values) => ({
            serviceTypeId: values.serviceTypeId == null ? 'Category is required' : null,
            plannedEndDate:
                values.plannedEndDate && toApiDateString(values.plannedEndDate) < job.startDate
                    ? 'Planned end date must be on or after start date'
                    : null,
        }),
    })

    useEffect(() => {
        setIsEditing(false)
        form.setValues(jobToFormValues(job))
    }, [job.id])

    useEffect(() => {
        if (!isEditing) {
            form.setValues(jobToFormValues(job))
        }
    }, [job.serviceTypeId, job.specificType, job.plannedEndDate, job.notes, isEditing])

    function startEditing() {
        form.setValues(jobToFormValues(job))
        setErrorMessage(null)
        setIsEditing(true)
    }

    function handleCancel() {
        form.setValues(jobToFormValues(job))
        setIsEditing(false)
        setErrorMessage(null)
    }

    async function handleSave() {
        const validation = form.validate()
        if (validation.hasErrors) {
            return
        }

        setSaving(true)
        setErrorMessage(null)
        try {
            const updatedJob = await jobDataLayer.patchJob(job.id, {
                jobName: job.name,
                serviceTypeId: form.values.serviceTypeId!,
                notes: form.values.notes.trim() || null,
                customerId: job.customerId,
                poNumber: form.values.poNumber.trim() || null,
                specificType: form.values.specificType.trim() || null,
                plannedEndDate: form.values.plannedEndDate
                    ? toApiDateString(form.values.plannedEndDate)
                    : null,
            })
            onJobUpdated(updatedJob)
            setIsEditing(false)
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Failed to save job details')
        } finally {
            setSaving(false)
        }
    }

    const specificTypes =
        serviceTypes.find((type) => type.id === form.values.serviceTypeId)?.specificTypes ?? []

    const selectData = [
        ...serviceTypes.map((st) => ({value: st.id, label: st.name})),
        ...(serviceTypes.some((st) => st.id === job.serviceTypeId)
            ? []
            : [{value: job.serviceTypeId, label: job.serviceTypeName}]),
    ]

    return (
        <Paper withBorder p="md" radius="md">
            <Stack gap="md">
                <Group justify="space-between" align="center">
                    <Title order={3}>Job details</Title>
                    {!isEditing ? (
                        canEdit ? (
                            <Button variant="subtle" size="compact-sm" onClick={startEditing}>
                                Edit
                            </Button>
                        ) : null
                    ) : (
                        <Group gap="sm">
                            <Button
                                variant="default"
                                size="compact-sm"
                                onClick={handleCancel}
                                disabled={saving}
                            >
                                Cancel
                            </Button>
                            <Button
                                size="compact-sm"
                                onClick={() => void handleSave()}
                                loading={saving}
                            >
                                Save
                            </Button>
                        </Group>
                    )}
                </Group>

                {errorMessage && (
                    <Text size="sm" c="red">
                        {errorMessage}
                    </Text>
                )}

                {isEditing ? (
                    <Stack gap="md">
                        <Group grow align="flex-start">
                            <Select
                                label="Category"
                                placeholder="Select category"
                                required
                                data={selectData}
                                searchable
                                {...form.getInputProps('serviceTypeId')}
                                onChange={(serviceTypeId) =>
                                    form.setValues({serviceTypeId, specificType: ''})
                                }
                            />
                            <Autocomplete
                                label="Specific type"
                                placeholder={
                                    specificTypes.length ? 'Pick or type' : 'Type it (optional)'
                                }
                                data={specificTypes}
                                {...form.getInputProps('specificType')}
                            />
                        </Group>
                        <DatePickerInput
                            label="Planned end date"
                            description="Expected end of the rental"
                            clearable
                            {...form.getInputProps('plannedEndDate')}
                            onChange={(value) =>
                                form.setFieldValue('plannedEndDate', parseDateInputValue(value))
                            }
                        />
                        <Textarea
                            label="Notes"
                            description="Shared with drivers on this job."
                            placeholder="Optional job notes"
                            minRows={2}
                            autosize
                            disabled={scheduleLocked}
                            {...form.getInputProps('notes')}
                        />
                        <TextInput
                            label="PO number (optional)"
                            {...form.getInputProps('poNumber')}
                        />
                        {scheduleLocked && (
                            <Text size="sm" c="dimmed">
                                {SCHEDULE_LOCKED_JOB_HINT}
                            </Text>
                        )}
                    </Stack>
                ) : (
                    <Stack gap="xs">
                        <Text size="sm">
                            Category: {job.serviceTypeName}
                            {job.specificType ? ` · ${job.specificType}` : ''}
                        </Text>
                        {job.plannedEndDate ? (
                            <Text size="sm">
                                Planned end date: {formatJobDate(job.plannedEndDate)}
                            </Text>
                        ) : null}
                        <Text size="sm">
                            PO number:{' '}
                            {job.poNumber ?? (
                                <Text component="span" c="dimmed">
                                    None
                                </Text>
                            )}
                        </Text>
                        <Text size="sm">
                            Billing terms: {projectBillingTermsLabel(jobBillingTerms)}
                        </Text>
                        {job.notes ? (
                            <FoldedText text={job.notes} label="Notes: " />
                        ) : (
                            <Text size="sm">
                                Notes:{' '}
                                <Text component="span" c="dimmed">
                                    None
                                </Text>
                            </Text>
                        )}
                        {scheduleLocked && (
                            <Text size="sm" c="dimmed">
                                {SCHEDULE_LOCKED_JOB_HINT}
                            </Text>
                        )}
                    </Stack>
                )}
            </Stack>
        </Paper>
    )
}
